import { Table } from "antd";
import moment from "moment";
import { type RouteComponentProps } from "react-router";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";

import { PaymentStatus } from "types/enum";

import { sum } from "utilities/math/math";
import { routes } from "utilities/routes";

import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTable, type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { MerchantPaymentStatusTag } from "commonComponents/entity/merchantPayment/common/MerchantPaymentStatus/MerchantPaymentStatus";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { RouteBillPayment } from "entity/bill/BillPayment/RouteBillPayment";
import "entity/invoicePayment/InvoicePaymentHistory/PaymentHistory.less";

export interface IBillPaymentForListData {
    paymentId: number;
    addedDate: string;
    scheduledDate?: string | null;
    paymentAmount: number;
    checkNumber: string;
    paymentMethod: string | null;
    status: PaymentStatus;
}

export class BillPaymentForList {
    constructor(data: IBillPaymentForListData) {
        this.paymentId = data.paymentId;
        this.addedDate = moment(data.addedDate);
        this.scheduledDate = data.scheduledDate ? moment(data.scheduledDate) : undefined;
        this.paymentAmount = data.paymentAmount;
        this.checkNumber = data.checkNumber;
        this.paymentMethod = data.paymentMethod ?? "";
        this.status = data.status;
    }
    paymentId: number;
    addedDate: moment.Moment;
    scheduledDate: moment.Moment | undefined;
    paymentAmount: number;
    checkNumber: string;
    paymentMethod: string;
    status: PaymentStatus;
}

interface IBillPaymentHistoryProps extends RouteComponentProps {
    payments: BillPaymentForList[];
    entityName: string;
    onReload: () => Promise<void>;
}

export const BillPaymentHistory: React.FunctionComponent<IBillPaymentHistoryProps> = ({
    payments,
    entityName,
    history,
    match,
    onReload,
}) => {
    const hasPayments = payments.length > 0;
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const billPartialPaymentsEnabled = builderInfo.flags.billPartialPayments;

    const billPaymentHistoryColumns: IBTColumnProps<BillPaymentForList>[] = [];

    const modalConfig: IModalConfiguration = {
        beforeClose: () => {
            void onReload();
            history.replace(match.url);
        },
        parentRoute: match.url,
    };

    billPaymentHistoryColumns.push({
        dataIndex: "addedDate",
        title: "Date",
        render: (paymentDate: moment.Moment, record: BillPaymentForList) =>
            record.status === PaymentStatus.Scheduled && record.scheduledDate ? (
                <span>
                    Scheduled date:{" "}
                    <DateDisplayDate
                        value={record.scheduledDate.toDate()}
                        data-testid="billPaymentScheduledDate"
                    />
                </span>
            ) : (
                <DateDisplay value={paymentDate} />
            ),
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentAmount",
        title: "Payment Amount",
        align: "right",
        render: (paymentAmount: number) => <CurrencyDisplay value={paymentAmount} />,
        className: "VerticalAlignRow",
    });

    if (billPartialPaymentsEnabled) {
        billPaymentHistoryColumns.push({
            dataIndex: "paymentMethod",
            title: "Payment method",
            className: "VerticalAlignRow",
        });
    } else {
        billPaymentHistoryColumns.push({
            dataIndex: "checkNumber",
            title: "Check #",
            className: "VerticalAlignRow",
        });
    }

    billPaymentHistoryColumns.push({
        dataIndex: "status",
        title: "Status",
        render: (status: PaymentStatus, record: BillPaymentForList) => {
            return (
                <MerchantPaymentStatusTag
                    status={status}
                    statusReason={
                        status === PaymentStatus.Scheduled && record.scheduledDate
                            ? `This payment is scheduled to send on ${record.scheduledDate.format(
                                  "MMMM D, YYYY",
                              )}. To cancel, void the payment before the scheduled date.`
                            : undefined
                    }
                />
            );
        },
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentId",
        render: (billPaymentId: number) => (
            <BTLinkRelative to={routes.billPayment.getDetailsPath(billPaymentId)}>
                View Details
            </BTLinkRelative>
        ),
    });

    const content = (
        <>
            {!hasPayments && (
                <span data-testid="billPaymentHistoryEmptyStateText">
                    Payments applied to this {entityName} will be listed here
                </span>
            )}
            {hasPayments && (
                <div className="BillPaymentHistoryScrollable">
                    <BTTable
                        dataSource={payments}
                        rowKey={(record) => record.paymentId.toString()}
                        columns={billPaymentHistoryColumns}
                        size="small"
                        pagination={false}
                        summary={(data) => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    {billPaymentHistoryColumns.map((x, index) => {
                                        if (x.title === "Payment Amount") {
                                            return (
                                                <Table.Summary.Cell
                                                    key={index}
                                                    index={index}
                                                    className="text-right"
                                                >
                                                    <strong>
                                                        Total:{" "}
                                                        <CurrencyDisplay
                                                            value={sum(
                                                                data.map((row) =>
                                                                    [
                                                                        PaymentStatus.Expired,
                                                                        PaymentStatus.Failed,
                                                                        PaymentStatus.Refunded,
                                                                        PaymentStatus.Void,
                                                                    ].includes(row.status)
                                                                        ? 0
                                                                        : row.paymentAmount,
                                                                ),
                                                            ).toNumber()}
                                                        />
                                                    </strong>
                                                </Table.Summary.Cell>
                                            );
                                        } else {
                                            return <Table.Summary.Cell key={index} index={index} />;
                                        }
                                    })}
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                </div>
            )}
        </>
    );

    return (
        <div data-testid="billPaymentHistory">
            <RouteBillPayment modalConfig={modalConfig} fromBill={true} />
            <PageSection>
                <BTRow>
                    <BTTitle level={3}>Payment history</BTTitle>
                </BTRow>
                {content}
            </PageSection>
        </div>
    );
};
